import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  NOTAS_FISCAIS_KEYS,
  type ListParams,
  listarNotasFiscais,
  listarColaboradoresLiberaveis,
  previewLiberacao,
  liberarNotaFiscal,
  cancelarLiberacao,
  enviarNotaFiscal,
  aprovarNotaFiscal,
  rejeitarNotaFiscal,
  faturarNotaFiscal,
} from '../api';

export function useNotasFiscais(params?: ListParams) {
  return useQuery({
    queryKey: NOTAS_FISCAIS_KEYS.list(params),
    queryFn: () => listarNotasFiscais(params),
    enabled: params !== undefined,
  });
}

export function useColaboradoresLiberaveis(search: string, enabled = true) {
  return useQuery({
    queryKey: NOTAS_FISCAIS_KEYS.releasableCollaborators(search),
    queryFn: () => listarColaboradoresLiberaveis(search || undefined),
    enabled,
  });
}

export function usePreviewLiberacao(collaboratorId?: string, referenceMonth?: string) {
  return useQuery({
    queryKey: NOTAS_FISCAIS_KEYS.preview(collaboratorId, referenceMonth),
    queryFn: () => previewLiberacao(collaboratorId!, referenceMonth!),
    enabled: !!collaboratorId && !!referenceMonth && /^\d{4}-\d{2}$/.test(referenceMonth),
  });
}

/** Toda ação muda a lista: invalida tudo sob `['notas-fiscais']`. */
function useNfMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: NOTAS_FISCAIS_KEYS.all }),
  });
}

export const useLiberarNotaFiscal = () => useNfMutation(liberarNotaFiscal);
export const useCancelarLiberacao = () => useNfMutation(cancelarLiberacao);
export const useEnviarNotaFiscal = () =>
  useNfMutation(({ id, number, file }: { id: string; number: string; file: File }) =>
    enviarNotaFiscal(id, { number, file }),
  );
export const useAprovarNotaFiscal = () => useNfMutation(aprovarNotaFiscal);
export const useRejeitarNotaFiscal = () =>
  useNfMutation(({ id, reason }: { id: string; reason: string }) => rejeitarNotaFiscal(id, reason));
export const useFaturarNotaFiscal = () => useNfMutation(faturarNotaFiscal);
